import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings as SettingsIcon, Save, RefreshCw, Database, Download, Trash2, Plus, HardDrive, Edit2, X } from 'lucide-react';
import api from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './Settings.css';

// Категории где разрешён CRUD (совпадает с backend KPI_CATEGORIES)
const KPI_CATS = ['KPI и аналитика', 'KPI сотрудников'];

const EMPTY_KPI_FORM = { label: '', key: '', value: '', value_type: 'integer', description: '', category: 'kpi' };

// Автогенерация технического ключа из названия (транслит + snake_case)
const TRANSLIT = {
  а:'a',б:'b',в:'v',г:'g',д:'d',е:'e',ё:'yo',ж:'zh',з:'z',и:'i',й:'y',
  к:'k',л:'l',м:'m',н:'n',о:'o',п:'p',р:'r',с:'s',т:'t',у:'u',ф:'f',
  х:'kh',ц:'ts',ч:'ch',ш:'sh',щ:'sch',ъ:'',ы:'y',ь:'',э:'e',ю:'yu',я:'ya',
};
const autoKeyFromLabel = (label, catKey) => {
  const prefix = catKey === 'employee_kpi' ? 'emp_kpi_' : 'kpi_';
  const slug = label.toLowerCase()
    .split('')
    .map(c => TRANSLIT[c] ?? ((/[a-z0-9]/.test(c)) ? c : '_'))
    .join('')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40);
  return prefix + (slug || 'param');
};

function Settings() {
  const { t } = useTranslation(['admin', 'common']);
  const [categories, setCategories] = useState({});
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [backups, setBackups] = useState([]);

  // KPI CRUD
  const [kpiModal, setKpiModal] = useState(null); // null | { mode: 'add'|'edit', catKey, item? }
  const [kpiForm, setKpiForm] = useState(EMPTY_KPI_FORM);
  const [kpiSaving, setKpiSaving] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupCreating, setBackupCreating] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchBackups();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.getSettingsByCategory();
      setCategories(res.data);
      // Собираем текущие значения в плоский объект {key: value}
      const vals = {};
      Object.values(res.data).forEach(items => {
        items.forEach(item => {
          vals[item.key] = item.value;
        });
      });
      setValues(vals);
    } catch {
      alert(t('admin:settings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    setBackupLoading(true);
    try {
      const res = await api.getBackups();
      setBackups(res.data);
    } catch {
      // бэкапы могут быть недоступны
    } finally {
      setBackupLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm(t('admin:backups.createConfirm'))) return;
    setBackupCreating(true);
    try {
      await api.createBackup();
      await fetchBackups();
    } catch {
      alert(t('admin:backups.createError'));
    } finally {
      setBackupCreating(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const res = await api.downloadBackup(filename);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert(t('admin:backups.downloadError'));
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(t('admin:backups.deleteConfirm', { filename }))) return;
    try {
      await api.deleteBackup(filename);
      await fetchBackups();
    } catch {
      alert(t('admin:backups.deleteError'));
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString(getDateLocale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Превращает "eabd_sqlite_20260304_152112.db" → "SQLite • 04.03.2026 15:21"
  const formatBackupName = (filename) => {
    const match = filename.match(/(\w+)_(\w+)_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
    if (!match) return filename;
    const [, , dbType, year, month, day, hour, min] = match;
    const type = dbType === 'sqlite' ? 'SQLite' : dbType === 'postgres' ? 'PostgreSQL' : dbType.toUpperCase();
    return `${type} • ${day}.${month}.${year} ${hour}:${min}`;
  };

  const handleChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.bulkUpdateSettings(values);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert(t('admin:settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Маппинг отображаемого имени категории → внутренний ключ
  const CAT_DISPLAY_TO_KEY = {
    'KPI и аналитика': 'kpi',
    'KPI сотрудников': 'employee_kpi',
  };

  const openAddKpi = (catDisplayName) => {
    const catKey = CAT_DISPLAY_TO_KEY[catDisplayName] || 'kpi';
    setKpiForm({ ...EMPTY_KPI_FORM, category: catKey });
    setKpiModal({ mode: 'add', catDisplayName });
  };

  const openEditKpi = (item, catDisplayName) => {
    setKpiForm({
      label: item.label || '',
      key: item.key,
      value: item.value,
      value_type: item.value_type || 'integer',
      description: item.description || '',
      category: CAT_DISPLAY_TO_KEY[catDisplayName] || 'kpi',
    });
    setKpiModal({ mode: 'edit', catDisplayName, itemId: item.id });
  };

  const handleKpiSave = async () => {
    if (!kpiForm.label || !kpiForm.key) {
      alert('Заполните название и ключ');
      return;
    }
    setKpiSaving(true);
    try {
      if (kpiModal.mode === 'add') {
        await api.createSystemSetting(kpiForm);
      } else {
        await api.updateSystemSetting(kpiModal.itemId, {
          label: kpiForm.label,
          value: kpiForm.value,
          value_type: kpiForm.value_type,
          description: kpiForm.description,
        });
      }
      setKpiModal(null);
      await fetchSettings();
    } catch (e) {
      alert('Ошибка: ' + (e.response?.data?.error || e.message));
    } finally {
      setKpiSaving(false);
    }
  };

  const handleKpiDelete = async (item) => {
    if (!window.confirm(t('admin:settings.kpi.deleteConfirm', { label: item.label || item.key }))) return;
    try {
      await api.deleteSystemSetting(item.id);
      await fetchSettings();
    } catch (e) {
      alert('Ошибка удаления: ' + (e.response?.data?.error || e.message));
    }
  };

  const renderField = (item) => {
    const val = values[item.key] ?? item.value;

    if (item.value_type === 'boolean') {
      return (
        <label className="setting-toggle">
          <input
            type="checkbox"
            checked={val === 'true' || val === true || val === '1'}
            onChange={e => handleChange(item.key, e.target.checked ? 'true' : 'false')}
          />
          <span className="toggle-slider" />
          <span className="toggle-label">{val === 'true' || val === true || val === '1' ? t('admin:settings.enabled') : t('admin:settings.disabled')}</span>
        </label>
      );
    }

    if (item.value_type === 'integer') {
      return (
        <input
          className="setting-input"
          type="number"
          value={val}
          onChange={e => handleChange(item.key, e.target.value)}
        />
      );
    }

    return (
      <input
        className="setting-input"
        type="text"
        value={val}
        onChange={e => handleChange(item.key, e.target.value)}
      />
    );
  };

  const getCategoryIcon = (catName) => {
    return t(`admin:settings.categoryIcons.${catName}`, { defaultValue: '⚙️' });
  };

  const getCategoryLabel = (catName) => {
    return t(`admin:settings.categoryNames.${catName}`, { defaultValue: catName });
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="settings-header">
          <h1><SettingsIcon size={24} /> {t('admin:settings.title')}</h1>
        </div>
        <div className="settings-loading">{t('admin:settings.loadingSettings')}</div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1><SettingsIcon size={24} /> {t('admin:settings.title')}</h1>
        <div className="settings-actions">
          <button className="btn-refresh" onClick={fetchSettings} title={t('common:buttons.refresh')}>
            <RefreshCw size={16} />
            {t('common:buttons.refresh')}
          </button>
          <button
            className={`btn-settings-save ${saved ? 'saved' : ''}`}
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={16} />
            {saving ? t('admin:settings.saving') : saved ? t('admin:settings.saved') : t('admin:settings.save')}
          </button>
        </div>
      </div>

      <div className="settings-grid">
        {Object.entries(categories).map(([catName, items]) => {
          const isKpiCat = KPI_CATS.includes(catName);
          return (
            <div className="settings-category-card" key={catName}>
              <div className="category-header">
                <span className="category-icon">{getCategoryIcon(catName)}</span>
                <h2>{getCategoryLabel(catName)}</h2>
                {isKpiCat && (
                  <button
                    onClick={() => openAddKpi(catName)}
                    style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', color: '#1e40af', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
                  >
                    <Plus size={14} /> {t('admin:settings.kpi.addParam')}
                  </button>
                )}
              </div>
              <div className="category-body">
                {items.map(item => (
                  <div className="setting-row" key={item.key}>
                    <div className="setting-info">
                      <div className="setting-label">
                        {t(`admin:settingLabels.${item.key}`, { defaultValue: item.label || item.key })}
                      </div>
                      {item.description && (
                        <div className="setting-description">
                          {t(`admin:settingDescriptions.${item.key}`, { defaultValue: item.description })}
                        </div>
                      )}
                      {isKpiCat && (
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>
                          {t('admin:settings.kpi.keyLabel')} <code>{item.key}</code>
                        </div>
                      )}
                    </div>
                    <div className="setting-control" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {renderField(item)}
                      {isKpiCat && (
                        <>
                          <button
                            onClick={() => openEditKpi(item, catName)}
                            title={t('common:buttons.edit')}
                            style={{ padding: '5px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', color: '#475569', display: 'flex' }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleKpiDelete(item)}
                            title={t('common:buttons.delete')}
                            style={{ padding: '5px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Модал добавления/редактирования KPI параметра */}
      {kpiModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', width: '460px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, color: '#1e3a8a', fontSize: '1.1rem' }}>
                {kpiModal.mode === 'add' ? `➕ ${t('admin:settings.kpi.newParam')}` : `✏️ ${t('admin:settings.kpi.editParam')}`}
              </h3>
              <button onClick={() => setKpiModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>

            {/* Название */}
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                {t('admin:settings.kpi.labelField')} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                value={kpiForm.label}
                onChange={e => {
                  const label = e.target.value;
                  setKpiForm(f => ({
                    ...f,
                    label,
                    ...(kpiModal.mode === 'add' ? { key: autoKeyFromLabel(label, f.category) } : {}),
                  }));
                }}
                placeholder={t('admin:settings.kpi.namePlaceholder')}
                style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>

            {/* Ключ */}
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                {t('admin:settings.kpi.keyField')} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                value={kpiForm.key}
                onChange={e => kpiModal.mode === 'add' && setKpiForm(f => ({ ...f, key: e.target.value }))}
                placeholder={t('admin:settings.kpi.autoKeyHint').slice(0, 30) + '...'}
                disabled={kpiModal.mode === 'edit'}
                style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', background: kpiModal.mode === 'edit' ? '#f8fafc' : '#fff', color: '#64748b', fontFamily: 'monospace' }}
              />
              <small style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: '3px', display: 'block' }}>
                {kpiModal.mode === 'add' ? t('admin:settings.kpi.autoKeyHint') : t('admin:settings.kpi.keyLockedHint')}
              </small>
            </div>

            {/* Значение */}
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>{t('admin:settings.kpi.valueField')}</label>
              <input
                value={kpiForm.value}
                onChange={e => setKpiForm(f => ({ ...f, value: e.target.value }))}
                placeholder={t('admin:settings.kpi.valuePlaceholder')}
                style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
              <small style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: '3px', display: 'block' }}>
                {t('admin:settings.kpi.valueHint')}
              </small>
            </div>

            {/* Описание */}
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>{t('admin:settings.kpi.descField')}</label>
              <input
                value={kpiForm.description}
                onChange={e => setKpiForm(f => ({ ...f, description: e.target.value }))}
                placeholder={t('admin:settings.kpi.descPlaceholder')}
                style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>{t('admin:settings.kpi.valueType')}</label>
              <select
                value={kpiForm.value_type}
                onChange={e => setKpiForm(f => ({ ...f, value_type: e.target.value }))}
                style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem' }}
              >
                <option value="integer">{t('admin:settings.kpi.typeInteger')}</option>
                <option value="string">{t('admin:settings.kpi.typeString')}</option>
                <option value="boolean">{t('admin:settings.kpi.typeBoolean')}</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setKpiModal(null)} style={{ padding: '0.6rem 1.2rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', color: '#475569' }}>
                {t('common:buttons.cancel')}
              </button>
              <button onClick={handleKpiSave} disabled={kpiSaving} style={{ padding: '0.6rem 1.2rem', background: 'linear-gradient(135deg, #1e40af, #1e3a8a)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
                {kpiSaving ? t('admin:settings.saving') : t('common:buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Секция бэкапов */}
      <div className="backup-section">
        <div className="backup-header">
          <h2><Database size={20} /> {t('admin:backups.title')}</h2>
          <button
            className="btn-create-backup"
            onClick={handleCreateBackup}
            disabled={backupCreating}
          >
            <Plus size={16} />
            {backupCreating ? t('admin:backups.creating') : t('admin:backups.createBackup')}
          </button>
        </div>

        <div className="backup-info">
          <HardDrive size={14} />
          <span>{t('admin:backups.autoInfo')}</span>
        </div>

        {backupLoading ? (
          <div className="backup-loading">{t('admin:backups.loadingList')}</div>
        ) : backups.length === 0 ? (
          <div className="backup-empty">{t('admin:backups.noBackups')}</div>
        ) : (
          <table className="backup-table">
            <thead>
              <tr>
                <th>{t('admin:backups.filename')}</th>
                <th>{t('admin:backups.size')}</th>
                <th>{t('admin:backups.createdAt')}</th>
                <th>{t('admin:backups.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {backups.map(b => (
                <tr key={b.filename}>
                  <td className="backup-filename">
                    <Database size={14} />
                    <span title={b.filename}>{formatBackupName(b.filename)}</span>
                  </td>
                  <td>{b.size_mb} {t('admin:backups.mb')}</td>
                  <td>{formatDate(b.created_at)}</td>
                  <td className="backup-actions">
                    <button
                      className="btn-backup-action download"
                      onClick={() => handleDownloadBackup(b.filename)}
                      title={t('admin:backups.download')}
                    >
                      <Download size={14} />
                    </button>
                    <button
                      className="btn-backup-action delete"
                      onClick={() => handleDeleteBackup(b.filename)}
                      title={t('common:buttons.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Settings;
